<template>
  <div class="space-y-3">
    <!-- No <form>: this component also sits inside the access-token form. -->
    <div class="flex flex-wrap items-start gap-2">
      <div class="w-40">
        <label :for="inputId" class="sr-only">6-digit verification code</label>
        <input
          :id="inputId"
          ref="input"
          :value="code"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          pattern="[0-9]{6}"
          maxlength="6"
          placeholder="123456"
          :disabled="verifying"
          :aria-invalid="Boolean(error)"
          :aria-describedby="error ? errorId : undefined"
          class="block w-full rounded-md bg-white px-3 py-1.5 text-center font-mono text-base tracking-[0.3em] text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-600 dark:focus:outline-indigo-500"
          data-testid="verification-code"
          @input="onInput"
          @paste="onPaste"
          @keydown.enter.prevent="verify"
        />
      </div>
      <button
        type="button"
        :disabled="verifying || code.length !== CODE_LENGTH"
        data-testid="verify-code"
        @click="verify"
        class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
      >
        {{ verifying ? 'Verifying…' : 'Verify' }}
      </button>
    </div>
    <p
      v-if="error"
      :id="errorId"
      class="text-sm text-red-600 dark:text-red-400"
      role="alert"
      data-testid="verification-error"
    >
      {{ error }}
    </p>
    <ResendVerificationButton ref="resend" @sent="onCodeSent" @already-verified="onVerified" />
  </div>
</template>

<script setup lang="ts">
import { ref, useId } from 'vue';
import { useAuthStore } from '@/stores/auth';
import ResendVerificationButton from '@/components/ResendVerificationButton.vue';

const CODE_LENGTH = 6;

const emit = defineEmits<{ verified: [] }>();

const auth = useAuthStore();

const inputId = `verification-code-${useId()}`;
const errorId = `${inputId}-error`;
const code = ref('');
const verifying = ref(false);
const error = ref<string | null>(null);
const input = ref<HTMLInputElement | null>(null);
const resend = ref<InstanceType<typeof ResendVerificationButton> | null>(null);

// Codes are often pasted with spaces or surrounding text, e.g. "123 456" or "Code: 123456".
function digitsOnly(value: string) {
  return value.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

function onInput(event: Event) {
  const target = event.target as HTMLInputElement;
  code.value = digitsOnly(target.value);
  target.value = code.value;
}

function onPaste(event: ClipboardEvent) {
  const pasted = event.clipboardData?.getData('text');
  if (pasted == null) return;
  event.preventDefault();
  code.value = digitsOnly(pasted);
  (event.target as HTMLInputElement).value = code.value;
}

function onCodeSent() {
  code.value = '';
  error.value = null;
  input.value?.focus();
}

// The auth store has already refreshed the account and subscription and shown the toast.
function onVerified() {
  code.value = '';
  error.value = null;
  emit('verified');
}

async function verify() {
  if (code.value.length !== CODE_LENGTH || verifying.value) return;
  verifying.value = true;
  error.value = null;
  try {
    const result = await auth.verifyEmail(code.value);
    if (!result) return;
    if (result.status === 'ok' || result.status === 'already_verified') {
      onVerified();
      return;
    }
    if (result.status === 'invalid_code') {
      const n = result.attemptsRemaining;
      error.value = `Wrong code, ${n} ${n === 1 ? 'attempt' : 'attempts'} left.`;
      input.value?.select();
      return;
    }
    error.value = 'This code has expired or was used up. Request a new one.';
    code.value = '';
    resend.value?.focus();
  } catch (e: unknown) {
    error.value = (e as { response?: unknown }).response
      ? 'Could not verify the code. Please try again later.'
      : 'Unable to connect to the server. Please check your network.';
  } finally {
    verifying.value = false;
  }
}
</script>
